declare global {
  interface Window {
    dataLayer: Record<string, unknown>[];
  }
}

type StageName = 'landing_page' | 'first_question' | 'screener_completed';

interface ScreenerStageParams {
  stage_name: StageName;
  job_id: string;
  applicant_guid: string;
  screener_question_count: number;
}

interface PermissionResultParams {
  permission_granted: boolean | 'deferred';
  applicant_guid: string;
}

export function pushScreenerStage(params: ScreenerStageParams): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: 'screener_stage',
    stage_name: params.stage_name,
    job_id: params.job_id,
    applicant_guid: params.applicant_guid,
    screener_question_count: params.screener_question_count,
    timestamp: new Date().toISOString(),
  };
  console.info('[GA] screener_stage →', payload);
  window.dataLayer.push(payload);
}

export function pushPermissionResult(params: PermissionResultParams): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: 'permission_result',
    permission_type: 'camera',
    permission_granted: params.permission_granted,
    interview_type: 'video',
    applicant_guid: params.applicant_guid,
    browser_type: navigator.userAgent,
    browser_version: navigator.appVersion,
  };
  console.info('[GA] permission_result →', payload);
  window.dataLayer.push(payload);
}

// VI-664: Platforms job bar. The shared <job-bar> component pushes its own hardcoded
// `jt_candidate_manager_*` dataLayer events; these supplementary `jt_video_screener_*` events
// let us track job-bar usage under the Video Screener app in GA/GTM.
interface VideoScreenerJobParams {
  job_id: number | string;
  req_id?: string;
  job_title?: string;
  job_location?: string;
  job_status?: string;
}

export function pushVideoScreenerJobSelect(params: VideoScreenerJobParams): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: 'jt_video_screener_job_select',
    feature: 'video_screener',
    job_id: String(params.job_id),
    req_id: params.req_id ?? '',
    job_title: params.job_title ?? '',
    job_location: params.job_location ?? '',
    job_status: params.job_status ?? '',
    timestamp: new Date().toISOString(),
  };
  console.info('[GA] jt_video_screener_job_select →', payload);
  window.dataLayer.push(payload);
}

export function pushVideoScreenerSearchWithoutJob(): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: 'jt_video_screener_job_search_without_job',
    feature: 'video_screener',
    timestamp: new Date().toISOString(),
  };
  console.info('[GA] jt_video_screener_job_search_without_job →', payload);
  window.dataLayer.push(payload);
}

export function triggerHiddenGtmClick(elementId: string): void {
  console.info('[GA] triggerHiddenGtmClick →', elementId);
  const el = document.getElementById(elementId);
  if (el) {
    el.click();
  }
}

export type ExitIntentPage = 'landing' | 'interview';
export type ExitIntentTrigger = 'mouse_leave' | 'idle';
export type ExitIntentAction =
  | 'shown'
  | 'start_interview'
  | 'send_reminder'
  | 'continue_interview'
  | 'remind_me_later';

interface ExitIntentParams {
  page: ExitIntentPage;
  trigger: ExitIntentTrigger;
  action: ExitIntentAction;
  applicant_guid: string;
  job_id?: string;
}

// VI-368: "Pick Your Answer" take selection events
export function pushTakeSelected(takeNumber: 1 | 2 | 3, applicantGuid: string, jobId?: string): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: `vi_interview_take${takeNumber}`,
    take_number: takeNumber,
    applicant_guid: applicantGuid,
    job_id: jobId ?? '',
    timestamp: new Date().toISOString(),
  };
  console.info(`[GA] vi_interview_take${takeNumber} →`, payload);
  window.dataLayer.push(payload);
  triggerHiddenGtmClick(`btn-take${takeNumber}`);
}

export function pushExitIntent(params: ExitIntentParams): void {
  window.dataLayer = window.dataLayer || [];
  const payload = {
    event: 'exit_intent',
    page: params.page,
    trigger: params.trigger,
    action: params.action,
    applicant_guid: params.applicant_guid,
    job_id: params.job_id ?? '',
    is_mobile: typeof window !== 'undefined' && window.innerWidth < 831,
    timestamp: new Date().toISOString(),
  };
  console.info('[GA] exit_intent →', payload);
  window.dataLayer.push(payload);
}
